import { useEffect, useState } from "react";
import api from "../api/axios.tsx";

type ExchangeRates = { [currency: string]: number };

type UseExchangeRatesResult = {
  rates: ExchangeRates | null;
  loading: boolean;
  error: string | null;
};

// Shared cache and fetch promise across hook instances
let cachedRates: ExchangeRates | null = null;
let cachedError: string | null = null;
let fetchPromise: Promise<ExchangeRates | null> | null = null;

export function useExchangeRates(
  baseCurrency: string = "USD"
): UseExchangeRatesResult {
  const [rates, setRates] = useState<ExchangeRates | null>(cachedRates);
  const [loading, setLoading] = useState(!cachedRates && !cachedError);
  const [error, setError] = useState<string | null>(cachedError);

  useEffect(() => {
    if (cachedRates || cachedError) {
      // Already have data or error cached, no need to fetch
      setRates(cachedRates);
      setError(cachedError);
      setLoading(false);
      return;
    }

    if (!fetchPromise) {
      // Start fetch and cache the promise
      fetchPromise = api.get("/exchange_rates")
        .then(response => {
          const exchangeRates = response.data[0]?.rates || null;
          cachedRates = exchangeRates;
          cachedError = null;
          return exchangeRates;
        })
        .catch(err => {
          cachedError = err.message || "Failed to load rates";
          cachedRates = null;
          return null;
        });
    }

    setLoading(true);
    setError(null);

    fetchPromise.then((exchangeRates) => {
      setRates(exchangeRates);
      setError(cachedError);
      setLoading(false);
    });
  }, [baseCurrency]);

  return { rates, loading, error };
}
